import type { StaffProfileData } from "services/api/company/staff.service";
import { fromApiPhoneNumber } from "utils/phoneNumber.logic";
import type {
  EmploymentDetailsForm,
  PaymentMethodDetailsForm,
  PersonalDetailsForm,
} from "../profilePanel.contract";
import { toLocalDateOrNull } from "../domains/employment/profilePanel.payload.logic";
import { DEFAULT_VACATION_TERMS } from "../../shared/staffVacation.logic";

export const EMPTY_PERSONAL_DETAILS: PersonalDetailsForm = {
  firstName: "",
  lastName: "",
  phoneNumber: "",
  email: "",
  personalIdentityNumber: "",
  teamIds: [],
};

export const EMPTY_EMPLOYMENT_DETAILS: EmploymentDetailsForm = {
  employmentType: "",
  role: "",
  employeeNumber: "",
  startDate: null,
  endDate: null,
  salary: "",
  salaryType: "",
  taxTable: "",
  paymentType: "",
  employeeCategory: "",
  workPercentage: "",
  agreement: "",
  noticeDays: "",
  vacationDays: "",
  workplace: "",
  ...DEFAULT_VACATION_TERMS,
};

export const EMPTY_PAYMENT_METHOD_DETAILS: PaymentMethodDetailsForm = {
  type: "BankAccount",
  value: "",
};

const toVacationPayoutTypeOrDefault = (
  value: string | null | undefined,
  fallback: string,
): string => {
  if (value === "Automatic" || value === "Manual") return value;
  return fallback;
};

const toDaysOrDefault = (
  value: number | null | undefined,
  fallback: string,
): string => {
  return value == null ? fallback : String(value);
};

const toLatestEmploymentNumber = (profile: StaffProfileData | null): string => {
  const current = profile?.employmentInfo?.current?.employeeNumber;
  if (current != null) return String(current);

  for (const employment of profile?.employments ?? []) {
    if (employment.employeeNumber != null) {
      return String(employment.employeeNumber);
    }
  }

  return "";
};

export const toPersonalDetailsForm = (
  profile: StaffProfileData | null,
): PersonalDetailsForm => {
  return {
    firstName: profile?.firstName ?? "",
    lastName: profile?.lastName ?? "",
    phoneNumber: fromApiPhoneNumber(profile?.phoneNumber),
    email: profile?.email ?? "",
    personalIdentityNumber: profile?.personalIdentityNumber ?? "",
    teamIds: profile?.workGroups.map((workGroup) => String(workGroup.id)) ?? [],
  };
};

export const toEmploymentDetailsForm = (
  profile: StaffProfileData | null,
): EmploymentDetailsForm => {
  const currentEmployment = profile?.employmentInfo?.current;
  const activeSalary =
    currentEmployment?.salaries?.find((salary) => salary.isActive) ?? null;
  const currentSalaryValue = activeSalary?.value ?? currentEmployment?.salary;

  return {
    employmentType: currentEmployment?.employmentType ?? "",
    role: currentEmployment?.role ?? "",
    employeeNumber:
      currentEmployment?.employeeNumber == null
        ? ""
        : String(currentEmployment.employeeNumber),
    startDate: toLocalDateOrNull(currentEmployment?.startDate),
    endDate: toLocalDateOrNull(currentEmployment?.endDate),
    salary: currentSalaryValue == null ? "" : String(currentSalaryValue),
    salaryType: currentEmployment?.salaryType ?? "",
    taxTable:
      currentEmployment?.taxTable == null
        ? ""
        : String(currentEmployment.taxTable),
    paymentType: currentEmployment?.paymentType ?? "",
    employeeCategory: currentEmployment?.employeeCategory ?? "",
    workPercentage:
      profile?.workPercentage == null
        ? ""
        : String(Math.round(profile.workPercentage * 100)),
    agreement: currentEmployment?.agreement ?? "",
    noticeDays:
      currentEmployment?.noticeDays == null
        ? ""
        : String(currentEmployment.noticeDays),
    vacationDays:
      currentEmployment?.vacationDays == null
        ? ""
        : String(currentEmployment.vacationDays),
    workplace: currentEmployment?.workplace ?? "",
    vacationPayoutType: toVacationPayoutTypeOrDefault(
      currentEmployment?.vacationPayoutType,
      DEFAULT_VACATION_TERMS.vacationPayoutType,
    ),
    maxSickLeaveDays: toDaysOrDefault(
      currentEmployment?.maxSickLeaveDays,
      DEFAULT_VACATION_TERMS.maxSickLeaveDays,
    ),
    maxCareOfChildDays: toDaysOrDefault(
      currentEmployment?.maxCareOfChildDays,
      DEFAULT_VACATION_TERMS.maxCareOfChildDays,
    ),
    maxParentalLeaveDays: toDaysOrDefault(
      currentEmployment?.maxParentalLeaveDays,
      DEFAULT_VACATION_TERMS.maxParentalLeaveDays,
    ),
    // advanceVacationDays is absent from the GET response - the API only accepts
    // it when writing an employment - so there is nothing to carry over and a new
    // employment always starts its own grant from zero.
    advanceVacationDays: DEFAULT_VACATION_TERMS.advanceVacationDays,
  };
};

export const toPaymentMethodDetailsForm = (
  profile: StaffProfileData | null,
): PaymentMethodDetailsForm => {
  const paymentMethod = profile?.paymentMethods[0] ?? null;

  return {
    type: paymentMethod?.type ?? "BankAccount",
    value: paymentMethod?.value ?? "",
  };
};

export const toEmployeeNumberForNewEmployment = (
  profile: StaffProfileData | null,
): string => {
  return toLatestEmploymentNumber(profile);
};
